import { isValidElement, useEffect, useRef, useState } from 'react';
import type { ViewProps } from 'react-native';
import {
  FlatList,
  Platform,
  ScrollView,
  useWindowDimensions,
  View,
} from 'react-native';
import { DataTable } from 'react-native-paper';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { Text } from '@/ui';

import { useTableCopy } from './use-table-copy';

function getExportCellValue(value: unknown) {
  if (value == null) {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  return '';
}

function exportCellViewProps(options: {
  row: number;
  col: number;
  exportValue?: string;
}): ViewProps {
  if (Platform.OS !== 'web') {
    return {};
  }

  return {
    dataSet: {
      exportCell: 'true',
      row: String(options.row),
      col: String(options.col),
      ...(options.exportValue ? { exportValue: options.exportValue } : {}),
    },
  } as ViewProps;
}

// Space left below the table when it reaches the bottom of the window.
const STICK_TO_BOTTOM_GAP = 36;
// Keeps the table usable when the window is very short.
const STICK_TO_BOTTOM_MIN_HEIGHT = 240;

interface Column {
  key: string;
  title: string;
  numeric?: boolean;
  style?: any;
  truncate?: boolean;
}

interface Row {
  key: string | number;
  id: string | number;
  [key: string]: any;
}

interface Props {
  columns: Column[];
  rows: Row[];
  page?: number;
  total?: number;
  rowsPerPage?: number;
  onPageChange?: (newPage: number) => void;
  style?: any;
  maxHeight?: number;
  /**
   * Lets the table grow from its current position down to the bottom of the
   * window, with the rows scrolling inside once they no longer fit. A table
   * with fewer rows stays as short as its content. Replaces `maxHeight`.
   */
  stickToBottom?: boolean;
  footerValues?: Row;
}

export default function FixedHeaderTable({
  columns,
  rows,
  page,
  total,
  rowsPerPage,
  onPageChange,
  style,
  maxHeight = 400,
  stickToBottom = false,
  footerValues,
}: Props) {
  const { styles, theme } = useStyles(stylesheet);
  const numberOfPages = Math.ceil(total / rowsPerPage);
  const [columnWidths, setColumnWidths] = useState(columns.map(() => 0));
  const containerRef = useRef<View>(null);
  const { height: windowHeight } = useWindowDimensions();
  const [topInWindow, setTopInWindow] = useState<number | null>(null);

  useTableCopy(containerRef);

  const measureTop = () => {
    containerRef.current?.measureInWindow((_x, y) => {
      setTopInWindow(y);
    });
  };

  useEffect(() => {
    if (stickToBottom) {
      measureTop();
    }
  }, [stickToBottom, windowHeight]);

  // Until the top edge is measured, fall back to the regular layout.
  const fillHeight =
    stickToBottom && topInWindow !== null
      ? Math.max(
          windowHeight - topInWindow - STICK_TO_BOTTOM_GAP,
          STICK_TO_BOTTOM_MIN_HEIGHT
        )
      : null;

  const onCellLayout = (colIndex: number, width: number) => {
    if (columns[colIndex]?.truncate) {
      return;
    }

    setColumnWidths((prev) => {
      const newWidths = [...prev];
      if (width > newWidths[colIndex]) {
        newWidths[colIndex] = width + 50; // padding buffer
      }
      return newWidths;
    });
  };

  const getColumnWidth = (col: Column, measuredWidth: number) => {
    if (col.truncate) {
      return col.style?.maxWidth ?? col.style?.minWidth;
    }

    return measuredWidth || col.style?.minWidth;
  };

  const renderCellContent = (col: Column, value: Row[string]) => {
    if (isValidElement(value)) {
      return value;
    }

    if (typeof value !== 'string' && typeof value !== 'number') {
      return value;
    }

    return (
      <Text
        numberOfLines={col.truncate ? 1 : undefined}
        ellipsizeMode={col.truncate ? 'tail' : undefined}
      >
        {value}
      </Text>
    );
  };

  const renderRow = ({ item, rowIndex }: { item: Row; rowIndex: number }) => (
    <DataTable.Row
      style={{
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.card.default.borderColor,
        paddingTop: 8,
        paddingBottom: 8,
      }}
    >
      {columns.map((col, i) => {
        const columnWidth = getColumnWidth(col, columnWidths[i]);
        const exportValue = getExportCellValue(item[col.key]);

        return (
          <DataTable.Cell
            key={col.key}
            numeric={col.numeric}
            style={{
              ...(columnWidth
                ? {
                    minWidth: columnWidth,
                    ...(col.truncate
                      ? { maxWidth: columnWidth, width: columnWidth }
                      : {}),
                  }
                : {}),
              ...col.style,
              ...(col.truncate ? { overflow: 'hidden' } : {}),
            }}
          >
            <View
              {...exportCellViewProps({
                row: rowIndex,
                col: i,
                exportValue,
              })}
              style={col.truncate ? styles.truncatedCell : undefined}
              onLayout={(e) => {
                onCellLayout(i, e.nativeEvent.layout.width);
              }}
            >
              {renderCellContent(col, item[col.key])}
            </View>
          </DataTable.Cell>
        );
      })}
    </DataTable.Row>
  );

  return (
    <View
      ref={containerRef}
      onLayout={stickToBottom ? measureTop : undefined}
      style={[
        styles.container,
        fillHeight === null
          ? styles.flexibleContainer
          : { maxHeight: fillHeight },
        style,
      ]}
    >
      {/* Horizontal scroll wrapping header + rows */}
      <ScrollView
        horizontal
        bounces={false}
        showsHorizontalScrollIndicator
        style={{ flex: 1 }}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <View
          style={{
            flex: 1,
          }}
        >
          {/* Header */}
          <DataTable>
            <DataTable.Header
              style={{
                borderBottomColor: theme.colors.card.default.borderColor,
                borderBottomWidth: 1,
              }}
            >
              {columns.map((col, i) => {
                const columnWidth = getColumnWidth(col, columnWidths[i]);

                return (
                  <DataTable.Title
                    key={col.key}
                    numeric={col.numeric}
                    style={{
                      ...(columnWidth
                        ? {
                            minWidth: columnWidth,
                            ...(col.truncate
                              ? { maxWidth: columnWidth, width: columnWidth }
                              : {}),
                          }
                        : {}),
                      ...col.style,
                      ...(col.truncate ? { overflow: 'hidden' } : {}),
                    }}
                  >
                    <View
                      {...exportCellViewProps({
                        row: 0,
                        col: i,
                        exportValue: col.title,
                      })}
                      style={col.truncate ? styles.truncatedCell : undefined}
                      onLayout={(e) => {
                        onCellLayout(i, e.nativeEvent.layout.width);
                      }}
                    >
                      <Text
                        type="caption1"
                        style={{ color: theme.colors.text.secondary }}
                        numberOfLines={col.truncate ? 1 : undefined}
                        ellipsizeMode={col.truncate ? 'tail' : undefined}
                      >
                        {col.title}
                      </Text>
                    </View>
                  </DataTable.Title>
                );
              })}
            </DataTable.Header>
          </DataTable>

          {/* Scrollable rows */}
          <FlatList
            data={rows}
            keyExtractor={(item) => item.key.toString()}
            renderItem={({ item, index }) =>
              renderRow({ item, rowIndex: index + 1 })
            }
            style={fillHeight === null ? { maxHeight } : styles.fillBody}
            showsVerticalScrollIndicator
          />

          {footerValues
            ? renderRow({ item: footerValues, rowIndex: rows.length + 1 })
            : null}
        </View>
      </ScrollView>

      {page !== undefined && total && rowsPerPage && onPageChange ? (
        <View style={styles.paginationContainer}>
          <DataTable.Pagination
            page={page}
            numberOfPages={numberOfPages}
            onPageChange={onPageChange}
            label={`${page * rowsPerPage + 1}-${Math.min(
              (page + 1) * rowsPerPage,
              total
            )} of ${total}`}
            theme={{
              colors: {
                text: theme.colors.text.primary,
                onSurface: theme.colors.text.primary,
                elevation: { level2: theme.colors.text.primary },
              },
            }}
          />
        </View>
      ) : null}
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    width: '100%',
    borderWidth: 1,
    borderColor: theme.colors.card.default.borderColor,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: theme.colors.background_1,
  },
  flexibleContainer: {
    flex: 1,
  },
  fillBody: {
    flex: 1,
  },
  paginationContainer: {
    marginTop: 8,
    alignSelf: 'flex-end',
  },
  truncatedCell: {
    width: '100%',
    overflow: 'hidden',
  },
}));
